<template>
  <div class="org-settings-ts">
    <v-form ref="formTS">
      <OrgSettingsDialogSection
        title="Ticket defaults"
        icon="mdi-ticket-confirmation-outline"
        description="Count, sorting, and scheduled service date preferences."
      >
        <div class="org-settings-ts__fields-grid">
          <OrgSettingsDialogField label="Ticket Count">
            <v-text-field
              color="primary"
              density="comfortable"
              variant="outlined"
              hide-details
              class="osd-field__input"
              v-model="ts.active_ticket_count"
            />
          </OrgSettingsDialogField>

          <OrgSettingsDialogField label="Sort Ticket By">
            <v-select
              color="primary"
              density="comfortable"
              variant="outlined"
              hide-details
              class="osd-field__input"
              :items="org.sort_ticket_by"
              item-title="text"
              item-value="value"
              v-model="ts.sort_ticket_by"
            />
          </OrgSettingsDialogField>

          <OrgSettingsDialogField
            label="Schedule Service Date(s)"
            class="org-settings-ts__field--wide"
          >
            <v-select
              color="primary"
              density="comfortable"
              variant="outlined"
              hide-details
              class="osd-field__input"
              :items="org.scheduled_service_dates_items"
              v-model="ts.scheduled_service_dates"
              multiple
              chips
              closable-chips
            />
          </OrgSettingsDialogField>
        </div>
      </OrgSettingsDialogSection>

      <OrgSettingsDialogSection
        title="Permissions"
        icon="mdi-shield-account-outline"
        description="Who can create, close, and manage tickets across roles."
      >
        <div class="org-settings-ts__toggle-list">
          <OrgSettingsToggleRow
            v-model="ts.enable_to_rise_ticket"
            label="Enable Dealer Ticket Creation"
          />
          <OrgSettingsToggleRow
            v-model="ts.enable_manual_territory_tagging"
            label="Enable Manual Territory Tagging"
          />
          <OrgSettingsToggleRow
            v-model="ts.enable_ticket_creation"
            label="Enable Service Representatives To Create Ticket"
          />
          <OrgSettingsToggleRow
            v-model="ts.enable_ticket_close"
            label="Enable Service Representatives To Close Ticket"
          />
          <OrgSettingsToggleRow
            v-model="ts.reopen_ticket"
            label="Re-Open Ticket"
          />
          <OrgSettingsToggleRow
            v-model="ts.enable_ticket_components_request"
            label="Enable to raise Request for Ticket Component"
          />
          <OrgSettingsToggleRow
            v-model="ts.is_access_control_enable"
            label="Enable Access Control"
          />
        </div>

        <OrgSettingsDialogField
          v-if="ts.reopen_ticket"
          label="Re-Open Duration"
          class="org-settings-ts__conditional-field"
        >
          <v-select
            color="primary"
            density="comfortable"
            variant="outlined"
            hide-details
            class="osd-field__input"
            :items="org.reopen_ticket_durationItems"
            v-model="ts.reopen_ticket_duration"
          />
        </OrgSettingsDialogField>

        <OrgSettingsDialogField
          label="Default Customer Type"
          class="org-settings-ts__conditional-field"
        >
          <div class="org-settings-ts__segmented">
            <button
              type="button"
              class="org-settings-ts__segment"
              :class="{
                'org-settings-ts__segment--active':
                  ts.default_customer_type === 'INDIVIDUAL',
              }"
              @click="ts.default_customer_type = 'INDIVIDUAL'"
            >
              Individual
            </button>
            <button
              type="button"
              class="org-settings-ts__segment"
              :class="{
                'org-settings-ts__segment--active':
                  ts.default_customer_type === 'BUSINESS',
              }"
              @click="ts.default_customer_type = 'BUSINESS'"
            >
              Business
            </button>
          </div>
        </OrgSettingsDialogField>
      </OrgSettingsDialogSection>

      <OrgSettingsDialogSection
        title="Attachments & verification"
        icon="mdi-paperclip-check"
        description="OTP, serial checks, and attachment requirements."
      >
        <div class="org-settings-ts__toggle-list">
          <OrgSettingsToggleRow
            v-model="ts.is_attachment_mandatory"
            label="Attachment Mandatory"
          />
          <OrgSettingsToggleRow
            v-model="ts.allow_otp_proxy"
            label="Allow Proxy Phone Number to Get OTP"
          />
          <OrgSettingsToggleRow
            v-model="org.auto_approval_for_own_stock_component_request"
            label="Auto Approval of Own Stock Component Request"
          />
          <OrgSettingsToggleRow
            v-model="org.verify_serial_number"
            label="Enable Verify Serial Number"
          />
          <OrgSettingsToggleRow
            v-model="org.is_customer_category_mandatory"
            label="Customer Category Mandatory while Creating Ticket"
          />
        </div>

        <OrgSettingsDialogField
          v-if="ts.is_attachment_mandatory"
          label="Number of Attachments"
          class="org-settings-ts__conditional-field"
        >
          <v-autocomplete
            color="primary"
            density="comfortable"
            variant="outlined"
            hide-details
            class="osd-field__input"
            :items="org.number_of_attachments_required_items"
            v-model="ts.number_of_attachments_required"
          />
        </OrgSettingsDialogField>
      </OrgSettingsDialogSection>

      <OrgSettingsDialogSection
        title="Payments & modules"
        icon="mdi-cash-multiple"
        description="Payment capture, FOC, cabinet, and close-ticket payment rules."
      >
        <div class="org-settings-ts__toggle-list">
          <OrgSettingsToggleRow
            v-model="org.enable_payment_option"
            label="Enable To Add Payment Details For Tickets (No Warranty/Extended Warranty)"
          />
          <OrgSettingsToggleRow
            v-model="org.enable_disable_foc"
            label="Enable / Disable FOC"
          />
          <OrgSettingsToggleRow
            v-model="org.enable_disable_cabinet"
            label="Enable / Disable Cabinet"
          />
          <OrgSettingsToggleRow
            v-model="org.enable_disable_ticket_pay"
            label="Eligible for payment while closing ticket"
          />
        </div>
      </OrgSettingsDialogSection>

      <OrgSettingsDialogSection
        title="Service location rules"
        icon="mdi-map-marker-radius-outline"
        description="Workplace geofencing and close-ticket timing constraints."
      >
        <div class="org-settings-ts__toggle-list">
          <OrgSettingsToggleRow
            v-model="org.start_service_at_workplace"
            label="Start Service at Work Place"
          />
          <OrgSettingsDialogField
            v-if="org.start_service_at_workplace"
            label="Start Distance in Meters"
            class="org-settings-ts__nested-field"
          >
            <v-text-field
              color="primary"
              density="comfortable"
              variant="outlined"
              hide-details
              type="number"
              class="osd-field__input"
              v-model="org.location_validation"
            />
          </OrgSettingsDialogField>

          <OrgSettingsToggleRow
            v-model="org.end_service_at_workplace"
            label="End Service at Work Place"
          />
          <OrgSettingsDialogField
            v-if="org.end_service_at_workplace"
            label="End Distance in Meters"
            class="org-settings-ts__nested-field"
          >
            <v-text-field
              color="primary"
              density="comfortable"
              variant="outlined"
              hide-details
              type="number"
              class="osd-field__input"
              v-model="org.end_location_validation"
            />
          </OrgSettingsDialogField>

          <OrgSettingsToggleRow
            v-model="org.ticket_close_after_work_start"
            label="Close Ticket After Work Start"
          />
        </div>
      </OrgSettingsDialogSection>

      <OrgSettingsDialogSection
        title="Repair & visibility"
        icon="mdi-wrench-outline"
        description="Field edit rights, repair policy, and ticket history access by role."
      >
        <div class="org-settings-ts__toggle-list org-settings-ts__toggle-list--compact">
          <OrgSettingsToggleRow
            v-model="org.enable_edit_warranty_details"
            label="Enable to edit warranty details"
          />
          <OrgSettingsToggleRow
            v-model="org.enable_edit_serial_number"
            label="Enable to edit Serial Number"
          />
        </div>

        <OrgSettingsDialogField label="Product Repair Settings">
          <v-autocomplete
            color="primary"
            variant="outlined"
            density="comfortable"
            hide-details
            class="osd-field__input"
            :items="org.product_repair_setting_items"
            item-title="text"
            item-value="value"
            v-model="ts.product_repair_or_replacement"
          />
        </OrgSettingsDialogField>

        <div class="org-settings-ts__visibility">
          <div class="org-settings-ts__visibility-intro">
            <span class="org-settings-ts__visibility-title"
              >Ticket History Visibility</span
            >
            <span class="org-settings-ts__visibility-desc"
              >Choose which roles can view ticket history. Each role is an
              independent on/off permission.</span
            >
          </div>
          <div class="org-settings-ts__visibility-grid">
            <div
              v-for="group in ticketHistoryVisibilityGroups"
              :key="group.id"
              class="org-settings-ts__visibility-card"
              :class="`org-settings-ts__visibility-card--${group.id}`"
            >
              <header class="org-settings-ts__visibility-card-header">
                <div class="org-settings-ts__visibility-card-heading">
                  <span class="org-settings-ts__visibility-card-icon">
                    <v-icon :icon="group.icon" size="16" />
                  </span>
                  <span>{{ group.title }}</span>
                </div>
                <button
                  type="button"
                  class="org-settings-ts__visibility-all"
                  @click="toggleAllVisibilityGroup(group)"
                >
                  {{
                    isAllVisibilityGroupOn(group) ? "Clear all" : "Select all"
                  }}
                </button>
              </header>
              <div class="org-settings-ts__visibility-roles">
                <div
                  v-for="role in group.roles"
                  :key="role.key"
                  class="org-settings-ts__visibility-role"
                >
                  <span class="org-settings-ts__visibility-role-label">{{
                    role.label
                  }}</span>
                  <button
                    type="button"
                    class="org-settings-twf__switch org-settings-twf__switch--sm"
                    :class="{
                      'org-settings-twf__switch--on': org[role.key],
                    }"
                    role="switch"
                    :aria-checked="!!org[role.key]"
                    :aria-label="`${role.label} ticket history visibility`"
                    @click="org[role.key] = !org[role.key]"
                  >
                    <span class="org-settings-twf__switch-thumb" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </OrgSettingsDialogSection>

      <OrgSettingsDialogSection
        title="RFS rights"
        icon="mdi-account-key-outline"
        description="Assign request-for-service rights by user type."
      >
        <div class="org-settings-ts__rfs-grid">
          <div class="org-settings-ts__rfs-table-wrap">
            <div class="org-settings-ts__rfs-table-label">Available types</div>
            <v-data-table
              density="compact"
              :items="org.user_typeItems"
              :headers="org.UserType_headers"
              item-text="text"
              item-value="value"
              class="org-settings-ts__rfs-table"
            >
              <template #item.type="{ item }">
                <span class="org-settings-ts__rfs-type">{{ item.text }}</span>
              </template>
              <template #item.actions="{ item, index }">
                <v-btn
                  size="x-small"
                  icon
                  variant="text"
                  @click="org.addUserTypeDialogMethod(item, index)"
                >
                  <v-icon color="green" size="18">mdi-plus-circle</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </div>
          <div class="org-settings-ts__rfs-table-wrap">
            <div class="org-settings-ts__rfs-table-label">Assigned rights</div>
            <v-data-table
              density="compact"
              :items="org.duplicate_typeItems"
              :headers="org.UserType_headers"
              item-text="text"
              item-value="value"
              class="org-settings-ts__rfs-table"
            >
              <template #item.type="{ item }">
                <span class="org-settings-ts__rfs-type">{{ item.text }}</span>
              </template>
              <template #item.actions="{ item, index }">
                <v-btn
                  size="x-small"
                  icon
                  variant="text"
                  @click.stop="org.delete_Duplicate_Method(item, index)"
                >
                  <v-icon color="red" size="18">mdi-minus-circle</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </div>
        </div>
      </OrgSettingsDialogSection>
    </v-form>
  </div>
</template>

<script>
import OrgSettingsToggleRow from "./OrgSettingsToggleRow.vue";
import OrgSettingsDialogSection from "./OrgSettingsDialogSection.vue";
import OrgSettingsDialogField from "./OrgSettingsDialogField.vue";

export default {
  name: "OrganizationSettingsTicketSettingsPanel",
  components: {
    OrgSettingsToggleRow,
    OrgSettingsDialogSection,
    OrgSettingsDialogField,
  },
  inject: ["orgSettings"],
  computed: {
    org() {
      return this.orgSettings;
    },
    ts() {
      return this.orgSettings.ticket_settings;
    },
    ticketHistoryVisibilityGroups() {
      return [
        {
          id: "enterprise",
          title: "Enterprise",
          icon: "mdi-office-building-outline",
          roles: [
            { label: "Manager", key: "manager" },
            { label: "Agent", key: "agent" },
            { label: "Technician", key: "representative" },
          ],
        },
        {
          id: "territory",
          title: "Territory",
          icon: "mdi-map-marker-radius",
          roles: [
            { label: "NSM", key: "nsm" },
            { label: "RSM", key: "rsm" },
          ],
        },
        {
          id: "dealer",
          title: "Dealer",
          icon: "mdi-store-outline",
          roles: [
            { label: "Owner", key: "dealer_owner" },
            { label: "Agent", key: "dealer_agent" },
            { label: "Technician", key: "dealer_representative" },
          ],
        },
        {
          id: "partner",
          title: "Service Partner",
          icon: "mdi-handshake-outline",
          roles: [
            { label: "Owner", key: "service_partner" },
            { label: "Agent", key: "partner_agent" },
            { label: "Technician", key: "partner_representative" },
          ],
        },
      ];
    },
  },
  methods: {
    isAllVisibilityGroupOn(group) {
      return group.roles.every((role) => !!this.org[role.key]);
    },
    toggleAllVisibilityGroup(group) {
      const next = !this.isAllVisibilityGroupOn(group);
      group.roles.forEach((role) => {
        this.org[role.key] = next;
      });
    },
  },
};
</script>
